import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { PageHeader } from '../components/PageHeader';
import { StatusBadge } from '../components/StatusBadge';
import { Plus, Check, X, Eye, Truck } from 'lucide-react';
import { formatDate, formatDateTime } from '../lib/format';
import { useToast } from '../hooks/use-toast';
import { EquipmentOptionLabel } from '../components/EquipmentCell';
import NewRequestWizard from '../components/NewRequestWizard';

const initialForm = {
  project_name: '', project_location: '', company_name: '', department: '',
  requested_category: '', requested_description: '', quantity: 1, purpose: '',
  date_needed: '', duration_days: 1, technical_specs: '', contact_person: '', contact_no: '',
};

// Deployment timezone is Asia/Manila (GMT+8, no DST). Return today's date in
// that zone as YYYY-MM-DD so the Date Needed picker's `min` matches the
// server-side anti-backdating guard regardless of the user's browser clock.
const todayManila = () => {
  const ms = Date.now() + 8 * 60 * 60 * 1000;
  return new Date(ms).toISOString().slice(0, 10);
};

const EquipmentRequests = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [dropdowns, setDropdowns] = useState({});
  const [tab, setTab] = useState('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [detail, setDetail] = useState(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveVehicleIds, setApproveVehicleIds] = useState([]);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  // Fleet-availability preview shown under Quantity in the New Request dialog.
  // Fetched whenever the user picks a category (debounced via effect deps).
  const [availability, setAvailability] = useState(null); // { category, available } | null
  const [availLoading, setAvailLoading] = useState(false);

  const canApprove = hasRole(user, 'super_admin','admin','approver');
  const canRequest = hasRole(user, 'super_admin','admin','requestor','supervisor');

  const load = useCallback(async () => {
    const r = await api.get('/equipment-requests');
    setRequests(r.data);
  }, []);
  const loadVehicles = useCallback(async (requestedCategory) => {
    const params = { status_filter: 'operational' };
    if (requestedCategory) params.category = requestedCategory;
    const r = await api.get('/vehicles', { params });
    setVehicles(r.data);
  }, []);
  const loadDropdowns = useCallback(async () => {
    const r = await api.get('/dropdowns');
    const g = {}; r.data.forEach((d) => { g[d.category] = g[d.category] || []; g[d.category].push(d.value); });
    setDropdowns(g);
  }, []);
  useEffect(() => { load(); loadDropdowns(); }, [load, loadDropdowns]);
  useEffect(() => { if (params.get('new') === '1' && canRequest) { setForm(initialForm); setOpen(true); params.delete('new'); setParams(params); } /* eslint-disable-next-line */ }, [params]);
  // Fetch fleet availability whenever the selected category (in the New Request
  // dialog) changes. Clears when the dialog closes or category is emptied.
  useEffect(() => {
    if (!open || !form.requested_category) { setAvailability(null); return; }
    let cancelled = false;
    setAvailLoading(true);
    (async () => {
      try {
        const r = await api.get('/vehicles/available-count', { params: { category: form.requested_category } });
        if (!cancelled) setAvailability(r.data);
      } catch (e) {
        if (!cancelled) setAvailability(null);
      } finally {
        if (!cancelled) setAvailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, form.requested_category]);

  const submit = async () => {
    // Client-side required-field checks. Project ID (stored server-side as
    // `project_location`) is now the key identifier for a request, alongside
    // Project Name, Equipment Category and Quantity.
    if (!form.project_name?.trim()) {
      toast({ title: 'Project Name is required', variant: 'destructive' }); return;
    }
    if (!form.project_location) {
      toast({ title: 'Project ID is required', variant: 'destructive' }); return;
    }
    if (!form.requested_category) {
      toast({ title: 'Equipment Category is required', variant: 'destructive' }); return;
    }
    try {
      await api.post('/equipment-requests', { ...form, quantity: Number(form.quantity), duration_days: form.duration_days ? Number(form.duration_days) : null });
      toast({ title: 'Request submitted' });
      setOpen(false); load();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' }); }
  };

  const openApprove = async (r) => {
    setDetail(r);
    // Pre-size the slot array to the requested quantity (default 1).
    const qty = Math.max(1, Number(r.quantity) || 1);
    setApproveVehicleIds(Array(qty).fill(''));
    await loadVehicles(r.requested_category);
    setApproveOpen(true);
  };
  const setApproveVehicleAt = (idx, value) => setApproveVehicleIds((prev) => prev.map((v, i) => (i === idx ? value : v)));
  const approveComplete = approveVehicleIds.length > 0 && approveVehicleIds.every((v) => !!v)
    && new Set(approveVehicleIds).size === approveVehicleIds.length; // no duplicates
  const doApprove = async () => {
    if (!approveComplete) { toast({ title: 'Select a distinct equipment for each requested unit', variant: 'destructive' }); return; }
    try {
      await api.post(`/equipment-requests/${detail.id}/approve`, { vehicle_ids: approveVehicleIds });
      const n = approveVehicleIds.length;
      toast({ title: 'Request approved', description: n > 1 ? `${n} inspections created (one per unit).` : 'Inspection auto-created.' });
      setApproveOpen(false); load();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' }); }
  };
  const openReject = (r) => { setDetail(r); setRejectReason(''); setRejectOpen(true); };
  const doReject = async () => {
    if (!rejectReason.trim()) { toast({ title: 'Reason is mandatory', variant: 'destructive' }); return; }
    try {
      await api.post(`/equipment-requests/${detail.id}/reject`, { rejection_reason: rejectReason });
      toast({ title: 'Request rejected' });
      setRejectOpen(false); load();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' }); }
  };

  const filtered = requests.filter((r) => tab === 'all' || r.status === tab);

  return (
    <div className="space-y-4">
      <PageHeader title="Equipment Requests" description="Submit and track equipment requests across the workflow."
        actions={canRequest && <Button onClick={() => setOpen(true)} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]" data-testid="new-request-btn"><Plus className="h-4 w-4 mr-1.5" /> New Request</Button>} />

      <div className="flex gap-2 border-b overflow-x-auto" data-testid="esd-requests-tabs">
        {['all','pending','approved','deployed','completed','rejected'].map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t ? 'border-[hsl(217,91%,24%)] text-[hsl(217,91%,24%)]' : 'border-transparent text-slate-500 hover:text-slate-900'}`}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
            {t !== 'all' && <span className="ml-1.5 text-xs bg-slate-100 px-1.5 py-0.5 rounded-full">{requests.filter((r) => r.status === t).length}</span>}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b text-left text-slate-600">
              <tr><th className="p-3">Reference #</th><th className="p-3">Project</th><th className="p-3">Equipment</th><th className="p-3">Qty</th><th className="p-3">Date Needed</th><th className="p-3">Requestor</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y">
              {filtered.length === 0 ? <tr><td colSpan={8} className="p-8 text-center text-slate-500">No requests in this view.</td></tr> :
                filtered.map((r) => (
                  <tr key={r.id} className="table-row-hover">
                    <td className="p-3 font-mono text-xs">{r.reference_number}</td>
                    <td className="p-3"><div className="font-medium">{r.project_name}</div><div className="text-xs text-slate-500">{r.project_location}</div></td>
                    <td className="p-3">{r.requested_category}<div className="text-xs text-slate-500">{r.requested_description}</div></td>
                    <td className="p-3">{r.quantity}</td>
                    <td className="p-3 text-xs">{formatDate(r.date_needed)}</td>
                    <td className="p-3 text-xs">{r.requested_by_username}</td>
                    <td className="p-3"><StatusBadge status={r.status} type="request" /></td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => setDetail(r)}><Eye className="h-4 w-4" /></Button>
                      {r.status === 'pending' && canApprove && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => openApprove(r)}><Check className="h-4 w-4 text-emerald-600" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => openReject(r)}><X className="h-4 w-4 text-red-600" /></Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* New Request 5-step Wizard */}
      <NewRequestWizard
        open={open}
        onOpenChange={setOpen}
        dropdowns={dropdowns}
        onSubmitted={() => load()}
      />

      {/* Detail view */}
      <Dialog open={!!detail && !approveOpen && !rejectOpen} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">Request <span className="font-mono text-sm">{detail?.reference_number}</span></DialogTitle>
            <DialogDescription>{detail?.project_name}</DialogDescription>
          </DialogHeader>
          {detail && <RequestDetail r={detail} />}
          <DialogFooter><Button variant="outline" onClick={() => setDetail(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Request</DialogTitle>
            <DialogDescription>
              Assign an <span className="font-semibold">operational</span> equipment to fulfill this request.
              {detail?.requested_category && (
                <> Showing only category: <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 text-[hsl(217,91%,24%)] border border-blue-200 text-[11px] font-medium">{detail.requested_category}</span></>
              )}
            </DialogDescription>
          </DialogHeader>
          {vehicles.length === 0 ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <div className="font-semibold mb-1">No matching equipment available</div>
              <div className="text-xs">
                There is no <span className="font-semibold">operational</span> equipment in category{' '}
                <span className="font-semibold">{detail?.requested_category || '—'}</span> right now. Please add or release equipment, then try again.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Assign Equipment ({vehicles.length} available in category)</Label>
                <div className="text-[11px] text-slate-500">
                  Requested quantity: <span className="font-semibold text-slate-800">{approveVehicleIds.length}</span>
                </div>
              </div>
              {approveVehicleIds.map((selectedId, idx) => {
                // Prevent duplicate selections across slots (any other slot's choice is disabled).
                const takenElsewhere = new Set(approveVehicleIds.filter((_, i) => i !== idx).filter(Boolean));
                return (
                  <div key={idx} className="flex items-center gap-2" data-testid={`approve-slot-${idx}`}>
                    <div className="text-[11px] font-medium text-slate-600 w-14 shrink-0">Unit {idx + 1}</div>
                    <Select value={selectedId || ''} onValueChange={(v) => setApproveVehicleAt(idx, v)}>
                      <SelectTrigger data-testid={`approve-vehicle-select-${idx}`}><SelectValue placeholder="Select equipment..." /></SelectTrigger>
                      <SelectContent>
                        {vehicles.map((v) => (
                          <SelectItem key={v.id} value={v.id} disabled={takenElsewhere.has(v.id)}>
                            <EquipmentOptionLabel vehicle={v} />
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
              {approveVehicleIds.length > 1 && (
                <div className="text-[11px] text-slate-500 italic">
                  One inspection will be auto-created per unit. All units stay linked to the same request.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)}>Cancel</Button>
            <Button
              onClick={doApprove}
              disabled={!approveComplete || vehicles.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="approve-submit-btn"
            >
              <Check className="h-4 w-4 mr-1.5" />
              {approveVehicleIds.length > 1
                ? `Approve & Create ${approveVehicleIds.length} Inspections`
                : 'Approve & Create Inspection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Request</DialogTitle><DialogDescription>Rejection reason is mandatory and will be visible to the requestor.</DialogDescription></DialogHeader>
          <Label>Reason *</Label>
          <Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
          <DialogFooter><Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button><Button variant="destructive" onClick={doReject}>Reject</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const STAGE_META = {
  in_inspection:          { label: 'In Inspection',        cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  awaiting_mobilization:  { label: 'Awaiting Mobilization', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  in_mobilization:        { label: 'In Mobilization',      cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  completed:              { label: 'Completed',            cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  cancelled:              { label: 'Cancelled',            cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  rejected:               { label: 'Rejected',             cls: 'bg-red-100 text-red-800 border-red-200' },
};

const UnitProgressStrip = ({ requestId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr('');
      try {
        const r = await api.get(`/equipment-requests/${requestId}/units`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || e.message || 'Failed to load unit progress');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [requestId]);

  if (loading) return <div className="text-[11px] text-slate-500 italic">Loading unit progress…</div>;
  if (err) return <div className="text-[11px] text-red-600 italic">{err}</div>;
  if (!data || data.units.length === 0) return null;

  const completed = data.units.filter((u) => u.stage === 'completed').length;
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="unit-progress-strip">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Per-Unit Progress</div>
        <div className="text-[11px] text-slate-600">
          {completed}/{data.units.length} completed
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {data.units.map((u) => {
          const meta = STAGE_META[u.stage] || STAGE_META.in_inspection;
          return (
            <div
              key={u.unit_index}
              className={`flex-1 min-w-[180px] rounded border px-2.5 py-2 text-xs ${meta.cls}`}
              data-testid={`unit-strip-item-${u.unit_index}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold">Unit {u.unit_index}</div>
                <div className="text-[10px] font-medium uppercase tracking-wide" data-testid={`unit-strip-stage-${u.unit_index}`}>{meta.label}</div>
              </div>
              <div className="mt-1 text-[11px] font-mono text-slate-700 truncate">{u.equipment_code || u.plate_no || u.vehicle_id || '—'}</div>
              {u.brand_model && <div className="text-[10px] text-slate-500 truncate">{u.brand_model}</div>}
              <div className="mt-1 text-[10px] text-slate-500 space-x-1 truncate">
                {u.inspection?.reference_number && <span title="Inspection">{u.inspection.reference_number}</span>}
                {u.mobilization?.reference_number && <span title="Mobilization">— {u.mobilization.reference_number}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const RequestDetail = ({ r }) => {
  const stages = [
    { key: 'request', label: 'Equipment Request', done: true },
    { key: 'inspection', label: 'Inspection', done: ['approved','deployed','completed'].includes(r.status) },
    { key: 'mobilization', label: 'Mobilization', done: ['deployed','completed'].includes(r.status) },
    { key: 'completed', label: 'Completed', done: r.status === 'completed' },
  ];
  const showUnitStrip = ['approved','deployed','completed'].includes(r.status);
  return (
    <div className="space-y-4">
      {/* Workflow Progress */}
      <div className="flex items-center justify-between">
        {stages.map((s, i) => (
          <React.Fragment key={s.key}>
            <div className="flex flex-col items-center text-center">
              <div className={`h-9 w-9 rounded-full grid place-items-center text-xs font-bold ${s.done ? 'bg-[hsl(217,91%,24%)] text-white' : 'bg-slate-200 text-slate-500'}`}>{i+1}</div>
              <div className="text-[11px] mt-1 max-w-[80px]">{s.label}</div>
            </div>
            {i < stages.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${stages[i+1].done ? 'bg-[hsl(217,91%,24%)]' : 'bg-slate-200'}`} />}
          </React.Fragment>
        ))}
      </div>
      {showUnitStrip && <UnitProgressStrip requestId={r.id} />}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Info label="Status"><StatusBadge status={r.status} type="request" /></Info>
        <Info label="Quantity">{r.quantity}</Info>
        <Info label="Category">{r.requested_category}</Info>
        <Info label="Date Needed">{formatDate(r.date_needed)}</Info>
        <Info label="Project ID">{r.project_location}</Info>
        <Info label="Duration">{r.duration_days} days</Info>
        <Info label="Requestor">{r.requested_by_username}</Info>
        <Info label="Contact">{r.contact_person} · {r.contact_no}</Info>
      </div>
      {r.purpose && <Info label="Purpose">{r.purpose}</Info>}
      {r.technical_specs && <Info label="Tech Specs">{r.technical_specs}</Info>}
      {r.rejection_reason && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="text-xs font-semibold text-red-800">Rejection reason</div>
          <div className="text-sm text-red-900 mt-1">{r.rejection_reason}</div>
        </div>
      )}
    </div>
  );
};

const Info = ({ label, children }) => (<div><div className="text-xs text-slate-500">{label}</div><div className="font-medium">{children}</div></div>);
const Field = ({ label, value, onChange, type = 'text', min, max }) => (<div><Label>{label}</Label><Input type={type} min={min} max={max} value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={`field-${label.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'')}`} /></div>);
const SelectField = ({ label, value, onChange, options }) => {
  const opts = options.map((o) => typeof o === 'string' ? { value: o, label: o } : o);
  return (<div><Label>{label}</Label><Select value={value || ''} onValueChange={onChange}><SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent>{opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select></div>);
};

export default EquipmentRequests;
